import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Box,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Button,
  FormHelperText,
  Stack,
  IconButton,
  Popover,
  Checkbox,
  ListItemText,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import WifiIcon from "@mui/icons-material/Wifi";
import { layoutCSS } from "../component/common/commonStyle";
import {
  useGetcategoryidQuery,
  useGetcategoryQuery,
  useGetRcscategoryQuery,
  useGetRcsAgentQuery,
} from "../store/services/rcstemplateApi";
import { useGetsuggestionQuery } from "../store/services/rcstemplateApi";
import { usePostRcsTemplateMutation } from "../store/services/rcstemplateApi";

// ─── Theme ────────────────────────────────────────────────────────────────────
const PURPLE = "#7c3aed";
const PURPLE_LIGHT = "#c4b5fd";
const FS = "0.875rem";
const FIELD_H = 36;

const glass = {
  background: "rgba(255,255,255,0.55)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.65)",
  boxShadow: "0 2px 10px rgba(124,58,237,0.06)",
};

// Multiline fields
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    borderRadius: "8px",
    fontSize: FS,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d6cdf3",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: PURPLE_LIGHT },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: PURPLE,
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root": { fontSize: FS, color: "#64748B" },
  "& .MuiInputLabel-root.Mui-focused": { color: PURPLE },
  "& .MuiInputBase-input": { fontSize: FS },
  "& .MuiSelect-select": { fontSize: FS },
  "& .MuiFormHelperText-root": { fontSize: "0.72rem", mx: 0, mt: "2px" },
};

// Single-line fields / dropdowns: one fixed height so rows line up
const smallSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: FIELD_H,
  },
  "& .MuiInputBase-input": {
    fontSize: FS,
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
  "& .MuiSelect-select": {
    fontSize: FS,
    py: 0,
    display: "flex",
    alignItems: "center",
    height: "100% !important",
    boxSizing: "border-box",
    minHeight: "unset",
  },
};

const menuItemSx = { fontSize: FS, minHeight: 30 };

const outlinedPurpleBtn = {
  textTransform: "none",
  fontSize: FS,
  fontWeight: 600,
  height: FIELD_H,
  px: 1.25,
  borderRadius: "8px",
  borderColor: PURPLE_LIGHT,
  color: PURPLE,
  background: "rgba(255,255,255,0.6)",
  whiteSpace: "nowrap",
  "&:hover": { borderColor: PURPLE, background: "rgba(124,58,237,0.06)" },
};

const pillBtn = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: FS,
  height: 34,
  px: 2.5,
  borderRadius: "20px",
  background: "#7c6cf6",
  boxShadow: "none",
  "&:hover": { background: PURPLE, boxShadow: "none" },
};

const SectionCard = ({ title, action, children, sx }) => (
  <Paper
    elevation={0}
    sx={{ ...glass, p: 1, mb: 0.75, borderRadius: "10px", ...sx }}
  >
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
          minHeight: 24,
        }}
      >
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A" }}>
          {title}
        </Typography>
        {action}
      </Box>
    )}
    {children}
  </Paper>
);

const UploadButton = ({ label, accept, onChange, inputKey }) => (
  <Button
    component="label"
    variant="outlined"
    startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
    sx={{ ...outlinedPurpleBtn, flexShrink: 0 }}
  >
    {label}
    <input key={inputKey} type="file" accept={accept} hidden onChange={onChange} />
  </Button>
);

const FileNameText = ({ name, placeholder }) => (
  <Typography
    noWrap
    sx={{
      fontSize: FS,
      color: name ? "#334155" : "#94a3b8",
      maxWidth: 240,
    }}
  >
    {name || placeholder}
  </Typography>
);

const rowSx = { display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "flex-start" };

// Typing indicator component
const TypingIndicator = () => (
  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", py: 0.5 }}>
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: "#555",
        animation: "pulse 1.2s infinite ease-in-out",
        "@keyframes pulse": {
          "0%, 60%, 100%": { transform: "scale(0.8)", opacity: 0.5 },
          "30%": { transform: "scale(1.2)", opacity: 1 },
        },
      }}
    />
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: "#555",
        animation: "pulse 1.2s infinite ease-in-out 0.2s",
      }}
    />
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: "#555",
        animation: "pulse 1.2s infinite ease-in-out 0.4s",
      }}
    />
  </Box>
);

// Constants
const MAX_CARDS = 10;
const SUGGESTION_LIMIT = 4;
const MAX_UNITS = 20;
const templateOptions = [
  { id: 1, name: "text" },
  { id: 2, name: "Rich Card" },
  { id: 3, name: "Carousel" },
];

// Next variable name: [VAR1], [VAR2], [VAR3] ...
const getNextVariable = (text = "") => {
  const nums = [...text.matchAll(/\[\s*VAR(\d+)\s*\]/gi)].map((m) => Number(m[1]));
  return ` [VAR${nums.length ? Math.max(...nums) + 1 : 1}]`;
};

const requiredFieldsMap = {
  1: ["suggestion_text", "suggestion_postback"],
  2: ["url", "application"],
  3: ["phone_number"],
  4: ["latitude", "longitude"],
  5: ["query"],
  6: ["location_query"],
  7: [
    "start_date_time",
    "end_date_time",
    "calendar_title",
    "calendar_description",
  ],
};

const TemplateForm = () => {
  // ========== State Declarations ==========
  const navigate = useNavigate();
  const [category, setCategory] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [templateCategory, setTemplateCategory] = useState("");
  const [postRcsTemplate, { isLoading }] = usePostRcsTemplateMutation();
  const { data: getSuggestions } = useGetsuggestionQuery();
  const { data: getcategory } = useGetcategoryQuery();
  const { data: getRcsCategory } = useGetRcscategoryQuery();
  const rcsCategoryOptions = Array.isArray(getRcsCategory?.data)
    ? getRcsCategory.data
    : Array.isArray(getRcsCategory?.categories)
      ? getRcsCategory.categories
      : Array.isArray(getRcsCategory?.data?.categories)
        ? getRcsCategory.data.categories
        : [];
  const { data: getRcsAgent } = useGetRcsAgentQuery();
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");

  const { data: getallagents } = useGetcategoryidQuery(category, {
    skip: !category,
  });
  const [agentDetails, setAgentDetails] = useState(null);

  const [formValues, setFormValues] = useState({
    template_name: "",
    mediaHeight: "",
  });
  const [errors, setErrors] = useState({});

  const [selectedTemplate, setSelectedTemplate] = useState({
    id: "",
    name: "",
  });
  const [agent, setAgent] = useState([]);
  const [currentTime, setCurrentTime] = useState("");

  // Text content
  const [smstext, setSmstext] = useState("");

  const [cards, setCards] = useState([
    {
      id: 1,
      title: "",
      fileName: "",
      carouselCardDescription: "",
      mediaFile: null,
      thumbnailFile: null,
      thumbnailFileName: "",
      media_type: "",
    },
  ]);
  const [activeCardId, setActiveCardId] = useState(1);
  const [cardWidth, setCardWidth] = useState("");
  const [mediaHeight, setMediaHeight] = useState("");

  const [cardTitle, setCardTitle] = useState("");
  const [cardDescription, setCardDescription] = useState("");
  const [richCardMediaHeight, setRichCardMediaHeight] = useState("");
  const [richCardDirection, setRichCardDirection] = useState("");
  const [cardOrientation, setCardOrientation] = useState("");

  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState("");
  const [fileType, setFileType] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [suggestions, setSuggestions] = useState([
    {
      button_no: 1,
      button_action_id: "",
      suggestion_text: "",
      suggestion_postback: "",
      application: "",
      url: "",
      calendar_description: "",
      calendar_title: "",
      start_date_time: dayjs(),
      end_date_time: dayjs().add(1, "hour"),
      location_query: "",
      latitude: "",
      longitude: "",
      phone_number: "",
    },
  ]);

  // ========== Utility Functions ==========
  const getActiveCard = () => cards.find((card) => card.id === activeCardId);

  const [activeMediaPreviewUrl, setActiveMediaPreviewUrl] = useState(null);
  const [activeThumbnailPreviewUrl, setActiveThumbnailPreviewUrl] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      setCurrentTime(`${hours}:${minutes}`);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (getallagents?.data) {
      setAgent(getallagents.data);
    }
  }, [getallagents?.data]);

  useEffect(() => {
    const card = getActiveCard();
    let mediaUrl = null;
    let thumbnailUrl = null;

    if (card?.mediaFile) {
      mediaUrl = URL.createObjectURL(card.mediaFile);
      setActiveMediaPreviewUrl(mediaUrl);
    } else {
      setActiveMediaPreviewUrl(null);
    }

    if (card?.thumbnailFile) {
      thumbnailUrl = URL.createObjectURL(card.thumbnailFile);
      setActiveThumbnailPreviewUrl(thumbnailUrl);
    } else {
      setActiveThumbnailPreviewUrl(null);
    }

    return () => {
      if (mediaUrl) URL.revokeObjectURL(mediaUrl);
      if (thumbnailUrl) URL.revokeObjectURL(thumbnailUrl);
    };
  }, [getActiveCard()?.mediaFile, getActiveCard()?.thumbnailFile]);

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const resetFileInput = (e) => {
    e.target.value = "";
    setFile(null);
    setFileUrl("");
    setFileType("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleTemplateNameChange = (e) => {
    let { name, value } = e.target;
    value = value.replace(/ /g, "_");
    if (value.length <= MAX_UNITS) {
      handleChange({ target: { name, value } });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === " ") {
      event.preventDefault();
      const caretPosition = event.target.selectionStart;
      const value = formValues.template_name;
      const newValue =
        value.slice(0, caretPosition) + "_" + value.slice(caretPosition);
      if (newValue.length <= MAX_UNITS) {
        handleChange({ target: { name: "template_name", value: newValue } });
      }
    }
  };

  const handletemplateChange = (event) => {
    const selectedId = Number(event.target.value);
    const selected = templateOptions.find((opt) => opt.id === selectedId);
    setSelectedTemplate(selected);
  };

  const handleCardOrientationChange = (event) => {
    setCardOrientation(event.target.value);
  };

  const handleFileChanges = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const isImage = /\.(jpe?g|png|gif)$/i.test(selectedFile.name);
    const isVideo = /^video\//i.test(selectedFile.type);

    if (!isImage && !isVideo) {
      toast.error("Only JPG, JPEG, PNG, GIF images or videos are allowed.");
      resetFileInput(e);
      return;
    }

    if (isImage && selectedFile.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2 MB.");
      resetFileInput(e);
      return;
    }

    if (isVideo && selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Video must be under 10 MB.");
      resetFileInput(e);
      return;
    }

    if (isImage) {
      const previewURL = URL.createObjectURL(selectedFile);
      const img = new Image();
      img.src = previewURL;

      img.onload = () => {
        const w = img.width;
        const h = img.height;

        let isValid = false;

        if (richCardMediaHeight === "Short" && cardOrientation === "Vertical") {
          isValid = w === 1440 && h === 480;
        } else if (richCardMediaHeight === "Medium" && cardOrientation === "Vertical") {
          isValid = w === 1440 && h === 720;
        } else if (cardOrientation === "Horizontal") {
          isValid = w === 768 && h === 1024;
        } else {
          toast.error("Please select valid orientation & media options first.");
          resetFileInput(e);
          return;
        }

        if (!isValid) {
          toast.error(`Invalid dimensions! Got: ${w} x ${h}`);
          resetFileInput(e);
          return;
        }

        setFile(selectedFile);
        setFileName(selectedFile.name);
        setFileType("image");
        setFileUrl(previewURL);
      };

      img.onerror = () => {
        toast.error("Could not load image.");
        resetFileInput(e);
      };

      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
    setFileType("video");
    setFileUrl(URL.createObjectURL(selectedFile));
  };

  const handleThumbnailChange = (e) => {
    const thumb = e.target.files?.[0];
    if (!thumb) return;

    const isImage = /\.(jpe?g|png)$/i.test(thumb.name);
    if (!isImage) {
      toast.error("Thumbnail must be JPG or PNG");
      return;
    }

    if (thumb.size > 100 * 1024) {
      toast.error("Thumbnail must be under 100 KB");
      return;
    }

    setThumbnailFile(thumb);
    setThumbnailPreview(URL.createObjectURL(thumb));
  };

  const handleCarouselThumbnailChange = (e) => {
    const thumb = e.target.files?.[0];
    if (!thumb) return;

    const isImage = /\.(jpe?g|png)$/i.test(thumb.name);
    if (!isImage) {
      toast.error("Thumbnail must be JPG or PNG");
      return;
    }

    if (thumb.size > 100 * 1024) {
      toast.error("Thumbnail must be under 100 KB");
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(thumb);

    img.onload = () => {
      URL.revokeObjectURL(img.src);
      updateActiveCard("thumbnailFile", thumb);
      updateActiveCard("thumbnailFileName", thumb.name);
    };

    img.onerror = () => {
      toast.error("Could not load thumbnail image for validation.");
    };
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handlePrev = () => {
    setActiveCardId((prevId) => {
      const currentIndex = cards.findIndex((card) => card.id === prevId);
      const prevIndex =
        currentIndex === 0 ? cards.length - 1 : currentIndex - 1;
      return cards[prevIndex].id;
    });
  };

  const handleNext = () => {
    setActiveCardId((prevId) => {
      const currentIndex = cards.findIndex((card) => card.id === prevId);
      const nextIndex =
        currentIndex === cards.length - 1 ? 0 : currentIndex + 1;
      return cards[nextIndex].id;
    });
  };

  const addCard = () => {
    if (cards.length < MAX_CARDS) {
      const nextId = cards.length ? cards[cards.length - 1].id + 1 : 1;
      setCards([
        ...cards,
        {
          id: nextId,
          title: "",
          fileName: "",
          carouselCardDescription: "",
          mediaFile: null,
          thumbnailFile: null,
          thumbnailFileName: "",
          media_type: "image",
        },
      ]);
      setActiveCardId(nextId);
    }
  };

  const removeCard = (id) => {
    if (cards.length <= 2) {
      toast.error("At least 2 cards are required for a carousel.");
      return;
    }
    const updated = cards.filter((card) => card.id !== id);
    setCards(updated);
    if (activeCardId === id && updated.length > 0) {
      setActiveCardId(updated[0].id);
    }
  };

  const updateActiveCard = (field, value) => {
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.id === activeCardId ? { ...card, [field]: value } : card
      )
    );
  };

  const handlesuggetionChange = (button_no, field, value) => {
    setSuggestions((prev) =>
      prev.map((s) =>
        s.button_no === button_no ? { ...s, [field]: value } : s
      )
    );
  };

  const handleRemove = (button_no) => {
    setSuggestions((prev) => prev.filter((s) => s.button_no !== button_no));
  };

  const handleAdd = () => {
    if (suggestions.length >= SUGGESTION_LIMIT) return;
    const nextId = suggestions.length
      ? Math.max(...suggestions.map((s) => s.button_no)) + 1
      : 1;
    setSuggestions([
      ...suggestions,
      {
        button_no: nextId,
        button_action_id: "",
        suggestion_text: "",
        suggestion_postback: "",
        application: "",
        url: "",
        calendar_description: "",
        calendar_title: "",
        start_date_time: dayjs(),
        end_date_time: dayjs().add(1, "hour"),
        location_query: "",
        latitude: "",
        longitude: "",
        phone_number: "",
      },
    ]);
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!selectedTemplate) {
      newErrors.selectedTemplate = "Please fill the field";
    }
    if (!templateCategory?.rcs_template_category_id) {
      newErrors.templateCategory = "Please fill the field";
    }

    switch (selectedTemplate?.name) {
      case "text":
        if (!smstext?.trim()) newErrors.smstext = "Please fill the field";
        if (!formValues.template_name?.trim())
          newErrors.template_name = "Please fill the field";
        if (suggestions && suggestions.length > 0) {
          suggestions.forEach((s) => {
            const fieldsToCheck = requiredFieldsMap[s.button_action_id] || [];
            fieldsToCheck.forEach((field) => {
              const value = s[field];
              if (
                value === undefined ||
                value === null ||
                (typeof value === "string" && value.trim() === "") ||
                (typeof value === "number" && isNaN(value))
              ) {
                newErrors[`${s.button_no}_${field}`] = `${field} is required`;
              }
            });
          });
        }
        break;

      case "Rich Card":
        if (!formValues.template_name?.trim())
          newErrors.template_name = "Please fill the field";

        if (cardOrientation === "Vertical") {
          if (!richCardMediaHeight) {
            newErrors.richCardMediaHeight = "Please fill the field";
          }
        } else if (cardOrientation === "Horizontal") {
          if (!richCardDirection) {
            newErrors.richCardDirection = "Please fill the field";
          }
        }

        if (!cardTitle?.trim()) newErrors.cardTitle = "Please fill the field";
        if (!cardDescription?.trim())
          newErrors.cardDescription = "Please fill the field";
        if (!cardOrientation)
          newErrors.cardOrientation = "Please fill the field";

        if (!file) {
          newErrors.fileUpload = "Please upload a file";
        }

        if (fileType === "video" && !thumbnailFile) {
          newErrors.thumbnailFile = "Thumbnail is required for videos";
        }

        if (suggestions && suggestions.length > 0) {
          suggestions.forEach((s) => {
            if (!s.button_action_id && !s.suggestion_text) return;
            const fieldsToCheck = requiredFieldsMap[s.button_action_id] || [];
            fieldsToCheck.forEach((field) => {
              if (!s[field] || s[field].trim() === "") {
                newErrors[`${s.button_no}_${field}`] = `${field} is required`;
              }
            });
          });
        }
        break;

      case "Carousel":
        if (!cards || cards.length < 2)
          newErrors.cards = "At least 2 cards are must in carousel";
        if (suggestions && suggestions.length > 0) {
          suggestions.forEach((s) => {
            if (!s.button_action_id && !s.suggestion_text) return;
            const fieldsToCheck = requiredFieldsMap[s.button_action_id] || [];
            fieldsToCheck.forEach((field) => {
              if (!s[field] || s[field].trim() === "") {
                newErrors[`${s.button_no}_${field}`] = `${field} is required`;
              }
            });
          });
        }
        if (!agent || agent.length === 0) newErrors.agent = "Please fill the field";
        break;

      default:
        newErrors.selectedTemplate = "Invalid template type";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    let dataToSubmit;

    switch (selectedTemplate?.name) {
      case "text":
        dataToSubmit = {
          TemplateCategory: categoryName,
          rcs_template_category_id: templateCategory.rcs_template_category_id,
          rcs_text: smstext,
          bot_id: agent.map((a) => a.bot_id),
          rcs_customer_bot_ids: agent.map((a) => a.rcs_customer_bot_id),
          rcs_template_type_id: selectedTemplate.id,
          isApproved: false,
          templateTypeName: selectedTemplate.name,
          template_name: formValues.template_name,
          operator_name: agent?.operater_name,
          route_id: agent?.route_id,
        };
        if (suggestions && Array.isArray(suggestions) && suggestions.length > 0) {
          const validSuggestions = suggestions.filter(
            (s) => s != null && (s.button_action_id || s.suggestion_text)
          );
          if (validSuggestions.length > 0) {
            dataToSubmit.buttons = validSuggestions;
          }
        }
        break;

      case "Rich Card":
        dataToSubmit = new FormData();
        dataToSubmit.append("TemplateCategory", categoryName);
        dataToSubmit.append(
          "rcs_template_category_id",
          templateCategory.rcs_template_category_id,
        );
        dataToSubmit.append(
          "rcs_customer_bot_ids",
          JSON.stringify(agent.map((a) => a.rcs_customer_bot_id))
        );
        dataToSubmit.append("rcs_template_type_id", selectedTemplate.id);
        dataToSubmit.append("templateTypeName", selectedTemplate.name);
        dataToSubmit.append(
          "bot_id",
          JSON.stringify(agent.map((a) => a.bot_id))
        );
        dataToSubmit.append("template_name", formValues.template_name);
        dataToSubmit.append("operator_name", agent?.operater_name);
        dataToSubmit.append("route_id", agent?.route_id);
        dataToSubmit.append("card_orientation", cardOrientation);
        if (cardOrientation === "Vertical") {
          dataToSubmit.append("media_details", richCardMediaHeight);
        } else if (cardOrientation === "Horizontal") {
          dataToSubmit.append("media_details", richCardDirection);
        }
        dataToSubmit.append("card_title", cardTitle);
        dataToSubmit.append("card_description", cardDescription);
        dataToSubmit.append("media_type", fileType);
        if (fileType === "image" && file) {
          dataToSubmit.append("richCardImage", file);
        }
        if (fileType === "video" && file) {
          dataToSubmit.append("richCardVideo", file);
        }
        if (fileType === "video" && thumbnailFile) {
          dataToSubmit.append("thumbnailFile", thumbnailFile);
          dataToSubmit.append("thumbnailFileName", thumbnailFile.name);
        }
        if (fileName) {
          dataToSubmit.append("fileName", fileName);
        }
        if (suggestions && Array.isArray(suggestions) && suggestions.length > 0) {
          const validSuggestions = suggestions.filter(
            (s) => s != null && (s.button_action_id || s.suggestion_text)
          );
          if (validSuggestions.length > 0) {
            dataToSubmit.append("buttons", JSON.stringify(validSuggestions));
          }
        }
        dataToSubmit.append("isApproved", false);
        break;

      case "Carousel":
        dataToSubmit = new FormData();
        dataToSubmit.append("TemplateCategory", categoryName);
        dataToSubmit.append(
          "rcs_template_category_id",
          templateCategory.rcs_template_category_id,
        );
        dataToSubmit.append(
          "rcs_customer_bot_ids",
          JSON.stringify(agent.map((a) => a.rcs_customer_bot_id))
        );
        dataToSubmit.append("rcs_template_type_id", selectedTemplate.id);
        dataToSubmit.append("templateTypeName", selectedTemplate.name);
        dataToSubmit.append(
          "bot_id",
          JSON.stringify(agent.map((a) => a.bot_id))
        );
        dataToSubmit.append("template_name", formValues.template_name);
        dataToSubmit.append("operator_name", agent?.operater_name);
        dataToSubmit.append("route_id", agent?.route_id);
        dataToSubmit.append("isApproved", true);
        if (suggestions && Array.isArray(suggestions) && suggestions.length > 0) {
          const validSuggestions = suggestions.filter(
            (s) => s != null && (s.button_action_id || s.suggestion_text)
          );
          if (validSuggestions.length > 0) {
            dataToSubmit.append("buttons", JSON.stringify(validSuggestions));
          }
        }
        dataToSubmit.append("card_width", cardWidth);
        dataToSubmit.append("rcs_mediaHeight", mediaHeight);
        cards.forEach((card, index) => {
          dataToSubmit.append(
            `cards[${index}]`,
            JSON.stringify({
              card_no: card.id,
              card_title: card.title,
              media_type: card.media_type,
              fileName: card.fileName,
              thumbnailFileName: card.thumbnailFileName,
              carouselCardDescription: card.carouselCardDescription,
            })
          );
          if (card.mediaFile) {
            if (card.media_type === "video") {
              dataToSubmit.append("carouselVideos", card.mediaFile);
            } else {
              dataToSubmit.append("carouselImages", card.mediaFile);
            }
          }
          if (card.thumbnailFile) {
            dataToSubmit.append("carouselThumbnails", card.thumbnailFile);
          }
        });
        break;

      default:
        dataToSubmit = {};
    }

    try {
      const response = await postRcsTemplate(dataToSubmit).unwrap();
      if (response?.success === true) {
        toast.success(response.message);
        setTimeout(() => navigate("/rcs-template-list"), 1500);
      }
    } catch (err) {
      console.error("API error:", err);
      if (Array.isArray(err?.data?.errors)) {
        err.data.errors.forEach((errorMessage) => {
          toast.error(errorMessage);
        });
      } else {
        toast.error(err?.data?.message || "An unknown error occurred");
      }
    }
  };

  // ========== Small render helpers ==========
  const addVariableLink = (
    <Typography
      variant="caption"
      component="span"
      sx={{ color: "#3366cc", cursor: "pointer", fontSize: "0.78rem" }}
    >
      + Add Variable
    </Typography>
  );

  const variableHelper = (onAdd, count, max) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
      <Typography
        component="span"
        sx={{ color: "#3366cc", cursor: "pointer", fontSize: "0.78rem" }}
        onClick={onAdd}
      >
        + Add Variable
      </Typography>
      <Typography component="span" sx={{ fontSize: "0.75rem" }}>
        {count}/{max}
      </Typography>
    </Box>
  );

  const suggestionHelper = (key) => (
    <span>
      {errors[key] || ""}
      {addVariableLink}
    </span>
  );

  // ========== Render ==========
  return (
    <Box sx={{ ...layoutCSS, p: 1 }}>
      {/* Title bar */}
      <Paper
        elevation={0}
        sx={{
          ...glass,
          display: "flex",
          alignItems: "center",
          px: 1,
          py: 0.6,
          mb: 1,
          borderRadius: "10px",
        }}
      >
        <IconButton
          onClick={() => window.history.back()}
          size="small"
          sx={{ mr: 0.5, p: 0.5 }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b" }}>
          Create RCS Template
        </Typography>
      </Paper>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
        {/* ── Left: form panel ── */}
        <Paper
          elevation={0}
          sx={{
            ...glass,
            background: "rgba(255,255,255,0.4)",
            p: 1,
            borderRadius: "14px",
            flex: "1 1 560px",
            minWidth: 0,
          }}
        >
          {/* Template details */}
          <SectionCard title="Template Details">
            <Box sx={rowSx}>
              {/* Template Name first */}
              <TextField
                name="template_name"
                label="Template Name"
                value={formValues.template_name}
                onChange={handleTemplateNameChange}
                onKeyDown={handleKeyDown}
                error={Boolean(errors.template_name)}
                helperText={
                  errors.template_name ||
                  `${formValues.template_name?.length ?? 0}/${MAX_UNITS}${
                    formValues.template_name?.length >= MAX_UNITS ? " (limit reached)" : ""
                  }`
                }
                required
                size="small"
                sx={{ ...smallSx, width: 200 }}
              />

              {/* Category second */}
              <FormControl
                required
                size="small"
                error={!!errors.category}
                sx={{ ...smallSx, width: 190 }}
              >
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  onChange={(event) => {
                    const selectedId = event.target.value;
                    const selectedItem = getcategory?.data?.find(
                      (item) => item.rcs_bot_type_id === selectedId
                    );
                    setCategory(selectedId);
                    setCategoryName(selectedItem?.bot_type_name || "");
                    setErrors((prev) => ({ ...prev, category: undefined }));
                  }}
                  label="Category"
                >
                  {getcategory?.data?.map((item) => (
                    <MenuItem
                      key={item.rcs_bot_type_id}
                      value={item.rcs_bot_type_id}
                      sx={menuItemSx}
                    >
                      {item.bot_type_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                required
                size="small"
                error={!!errors.templateCategory}
                sx={{ ...smallSx, width: 210 }}
              >
                <InputLabel>Template Category</InputLabel>
                <Select
                  value={templateCategory}
                  onChange={(event) => {
                    setTemplateCategory(event.target.value);
                    setErrors((prev) => ({ ...prev, templateCategory: undefined }));
                  }}
                  label="Template Category"
                >
                  {rcsCategoryOptions.map((item) => (
                    <MenuItem
                      key={item.rcs_template_category_id}
                      value={item}
                      sx={menuItemSx}
                    >
                      {item.rcs_template_category_name ||
                        item.template_category_name ||
                        item.category_name ||
                        item.name ||
                        item.category}
                    </MenuItem>
                  ))}
                </Select>
                {errors.templateCategory && (
                  <FormHelperText>{errors.templateCategory}</FormHelperText>
                )}
              </FormControl>

              {category && (
                <FormControl
                  required
                  size="small"
                  error={!!errors.agent}
                  sx={{ ...smallSx, width: 210 }}
                >
                  <InputLabel>Agent</InputLabel>
                  <Select
                    multiple
                    value={agent}
                    disabled={!category}
                    onChange={(event) => {
                      const {
                        target: { value },
                      } = event;
                      setAgent(typeof value === "string" ? value.split(",") : value);
                      setErrors((prev) => ({ ...prev, agent: undefined }));
                    }}
                    renderValue={(selected) =>
                      selected.map((s) => s.agent_name).join(", ")
                    }
                    label="Agent"
                  >
                    {getallagents?.data?.map((item) => (
                      <MenuItem
                        key={item.rcs_customer_bot_id}
                        value={item}
                        dense
                        sx={menuItemSx}
                      >
                        <Checkbox
                          size="small"
                          checked={agent.some(
                            (a) => a.rcs_customer_bot_id === item.rcs_customer_bot_id
                          )}
                        />
                        <ListItemText
                          primary={item.agent_name}
                          primaryTypographyProps={{ fontSize: FS }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.agent && <FormHelperText>{errors.agent}</FormHelperText>}
                </FormControl>
              )}

              <FormControl
                size="small"
                error={!!errors.selectedTemplate}
                sx={{ ...smallSx, width: 160 }}
              >
                <InputLabel>Template Type</InputLabel>
                <Select
                  name="templateType"
                  value={selectedTemplate.id || ""}
                  onChange={handletemplateChange}
                  label="Template Type"
                >
                  {templateOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id} sx={menuItemSx}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.selectedTemplate && (
                  <FormHelperText>{errors.selectedTemplate}</FormHelperText>
                )}
              </FormControl>
            </Box>
          </SectionCard>

          {/* ── Text template ── */}
          {selectedTemplate?.name === "text" && (
            <SectionCard title="Template Text">
              <TextField
                variant="outlined"
                rows={6}
                label="Template Text"
                multiline
                size="small"
                fullWidth
                sx={fieldSx}
                value={smstext}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 2500) {
                    setSmstext(newValue);
                  }
                }}
                required
                error={Boolean(errors.smstext)}
                helperText={
                  errors.smstext ? (
                    errors.smstext
                  ) : (
                    variableHelper(
                      () => {
                        const currentText = smstext || "";
                        const variableCount = (currentText.match(/\[/g) || []).length;
                        if (variableCount >= 10) return;
                        const updatedText = currentText + getNextVariable(currentText);
                        if (updatedText.length <= 2500) {
                          setSmstext(updatedText);
                        }
                      },
                      smstext.length,
                      2500
                    )
                  )
                }
              />
            </SectionCard>
          )}

          {/* ── Rich Card template ── */}
          {selectedTemplate?.name === "Rich Card" && (
            <SectionCard title="Rich Card">
              <Box sx={rowSx}>
                <FormControl
                  required
                  size="small"
                  error={Boolean(errors.cardOrientation)}
                  sx={{ ...smallSx, width: 180 }}
                >
                  <InputLabel>Card Orientation</InputLabel>
                  <Select
                    name="cardOriention"
                    value={cardOrientation}
                    onChange={handleCardOrientationChange}
                    label="Card Orientation"
                  >
                    <MenuItem value="Vertical" sx={menuItemSx}>Vertical</MenuItem>
                    <MenuItem value="Horizontal" sx={menuItemSx}>Horizontal</MenuItem>
                  </Select>
                  {errors.cardOrientation && (
                    <FormHelperText>{errors.cardOrientation}</FormHelperText>
                  )}
                </FormControl>

                {cardOrientation === "Vertical" && (
                  <FormControl
                    required
                    size="small"
                    error={Boolean(errors.richCardMediaHeight)}
                    sx={{ ...smallSx, width: 160 }}
                  >
                    <InputLabel>Media Height</InputLabel>
                    <Select
                      value={richCardMediaHeight}
                      onChange={(e) => setRichCardMediaHeight(e.target.value)}
                      label="Media Height"
                    >
                      <MenuItem value="Short" sx={menuItemSx}>Short</MenuItem>
                      <MenuItem value="Medium" sx={menuItemSx}>Medium</MenuItem>
                    </Select>
                    {errors.richCardMediaHeight && (
                      <FormHelperText>{errors.richCardMediaHeight}</FormHelperText>
                    )}
                  </FormControl>
                )}

                {cardOrientation === "Horizontal" && (
                  <FormControl
                    required
                    size="small"
                    error={Boolean(errors.richCardDirection)}
                    sx={{ ...smallSx, width: 160 }}
                  >
                    <InputLabel>Card Direction</InputLabel>
                    <Select
                      value={richCardDirection}
                      onChange={(e) => {
                        setRichCardDirection(e.target.value);
                        setErrors((prev) => ({ ...prev, richCardDirection: undefined }));
                      }}
                      label="Card Direction"
                    >
                      <MenuItem value="Right" sx={menuItemSx}>Right</MenuItem>
                      <MenuItem value="Left" sx={menuItemSx}>Left</MenuItem>
                    </Select>
                    {errors.richCardDirection && (
                      <FormHelperText>{errors.richCardDirection}</FormHelperText>
                    )}
                  </FormControl>
                )}
              </Box>

              {/* Media upload */}
              <Box sx={{ ...rowSx, alignItems: "center", mt: 0.75 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography sx={{ fontSize: FS, fontWeight: 600 }}>Image/Video *</Typography>
                  <InfoIcon
                    sx={{ ml: 0.5, fontSize: "1rem", color: "#64748B" }}
                    aria-owns={open ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                  />
                  <Popover
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                      "& .MuiPopover-paper": { maxWidth: 300, width: "100%" },
                    }}
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1, fontSize: "0.9rem", opacity: 0.8, textAlign: "center" }}>
                      {richCardMediaHeight === "Short" && cardOrientation === "Vertical" &&
                        "The image you specify must be 3:1 aspect ratio, have a max file size of 2 MB, have an optimal resolution of 1440 pixels x 480 pixels, and should be a JPG,JPEG,PNG, or GIF. If you are uploading a video, the max file size is 10 MB."}
                      {richCardMediaHeight === "Medium" && cardOrientation === "Vertical" &&
                        "The image you specify must be 2:1 aspect ratio, have a max file size of 2 MB, have an optimal resolution of 1440 pixels x 720 pixels, and should be a JPG,JPEG,PNG, or GIF. If you are uploading a video, the max file size is 10 MB."}
                      {richCardDirection === "Left" && cardOrientation === "Horizontal" &&
                        "The image you specify must be 3:4 aspect ratio, have a max file size of 2 MB, have an optimal resolution of 768 pixels x 1024 pixels, and should be a JPG,JPEG,PNG, or GIF. If you are uploading a video, the max file size is 10 MB."}
                      {richCardDirection === "Right" && cardOrientation === "Horizontal" &&
                        "The image you specify must be 3:4 aspect ratio, have a max file size of 2 MB, have an optimal resolution of 768 pixels x 1024 pixels, and should be a JPG,JPEG,PNG, or GIF. If you are uploading a video, the max file size is 10 MB."}
                      {richCardDirection === "" && cardOrientation === "" && "please select orientation"}
                    </Typography>
                  </Popover>
                </Box>
                <UploadButton
                  label="Upload File"
                  accept=".jpeg,.jpg,.png,.gif,video/*"
                  onChange={handleFileChanges}
                />
                <FileNameText name={fileName} placeholder="No file selected" />
                {errors.fileUpload && (
                  <FormHelperText error sx={{ m: 0 }}>{errors.fileUpload}</FormHelperText>
                )}
              </Box>

              {fileType === "video" && (
                <Box sx={{ ...rowSx, alignItems: "center", mt: 0.75 }}>
                  <Typography sx={{ fontSize: FS, fontWeight: 600 }}>Video Thumbnail *</Typography>
                  <UploadButton
                    label="Upload"
                    accept=".jpeg,.jpg,.png"
                    onChange={handleThumbnailChange}
                  />
                  <FileNameText
                    name={thumbnailFile ? thumbnailFile.name : ""}
                    placeholder="No thumbnail selected"
                  />
                  {errors.thumbnailFile && (
                    <FormHelperText error sx={{ m: 0 }}>{errors.thumbnailFile}</FormHelperText>
                  )}
                </Box>
              )}

              {/* Card Title - shortened width, single line */}
              <TextField
                label="Card Title"
                variant="outlined"
                size="small"
                sx={{ ...fieldSx, mt: 0.75, width: 300, maxWidth: "100%" }}
                value={cardTitle}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 200) setCardTitle(newValue);
                }}
                required
                error={Boolean(errors.cardTitle)}
                helperText={
                  errors.cardTitle ||
                  variableHelper(
                    () => {
                      const current = cardTitle || "";
                      if ((current.match(/\[/g) || []).length >= 10) return;
                      setCardTitle(
                        current + getNextVariable(`${cardTitle} ${cardDescription}`)
                      );
                    },
                    cardTitle.length,
                    200
                  )
                }
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Card Description"
                rows={3}
                multiline
                size="small"
                sx={{ ...fieldSx, mt: 0.75 }}
                value={cardDescription}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 2500) setCardDescription(newValue);
                }}
                required
                error={Boolean(errors.cardDescription)}
                helperText={
                  errors.cardDescription ||
                  variableHelper(
                    () => {
                      const current = cardDescription || "";
                      setCardDescription(
                        current + getNextVariable(`${cardTitle} ${cardDescription}`)
                      );
                    },
                    cardDescription.length,
                    2500
                  )
                }
              />
            </SectionCard>
          )}

          {/* ── Carousel template ── */}
          {selectedTemplate?.name === "Carousel" && (
            <SectionCard title="Carousel">
              <Box sx={rowSx}>
                <FormControl size="small" sx={{ ...smallSx, width: 160 }}>
                  <InputLabel>Card Width</InputLabel>
                  <Select
                    name="cardWidth"
                    value={cardWidth}
                    onChange={(e) => setCardWidth(e.target.value)}
                    label="Card Width"
                  >
                    <MenuItem value="Small" sx={menuItemSx}>Small</MenuItem>
                    <MenuItem value="Medium" sx={menuItemSx}>Medium</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ ...smallSx, width: 160 }}>
                  <InputLabel>Media Height</InputLabel>
                  <Select
                    name="mediaHeight"
                    value={mediaHeight}
                    onChange={(e) => {
                      const newHeight = e.target.value;
                      setMediaHeight(newHeight);
                      setCards((prevCards) =>
                        prevCards.map((card) => ({
                          ...card,
                          fileName: "",
                          mediaFile: null,
                          media_type: "",
                          thumbnailFile: null,
                          thumbnailFileName: "",
                        }))
                      );
                    }}
                    label="Media Height"
                  >
                    <MenuItem value="Short" sx={menuItemSx}>Short</MenuItem>
                    <MenuItem value="Medium" sx={menuItemSx}>Medium</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Card tabs */}
              <Box sx={{ ...rowSx, mt: 0.75 }}>
                {cards.map((card) => (
                  <Button
                    key={card.id}
                    variant={card.id === activeCardId ? "contained" : "outlined"}
                    onClick={() => setActiveCardId(card.id)}
                    size="small"
                    sx={
                      card.id === activeCardId
                        ? {
                            ...pillBtn,
                            height: 30,
                            px: 1.5,
                            borderRadius: "8px",
                          }
                        : { ...outlinedPurpleBtn, height: 30 }
                    }
                  >
                    <span>Card {card.id}</span>
                    {cards.length > 2 && (
                      <span
                        style={{ marginLeft: 8, cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCard(card.id);
                        }}
                      >
                        ✕
                      </span>
                    )}
                  </Button>
                ))}
                {cards.length < MAX_CARDS && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={addCard}
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    sx={{ ...outlinedPurpleBtn, height: 30 }}
                  >
                    Add Card
                  </Button>
                )}
              </Box>
              {errors.cards && (
                <FormHelperText error sx={{ mx: 0 }}>{errors.cards}</FormHelperText>
              )}

              {/* Media upload */}
              <Box sx={{ ...rowSx, alignItems: "center", mt: 0.75 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography sx={{ fontSize: FS, fontWeight: 600 }}>Image/Video *</Typography>
                  <InfoIcon
                    sx={{ ml: 0.5, fontSize: "1rem", color: "#64748B" }}
                    aria-owns={open ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                  />
                  <Popover
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                      "& .MuiPopover-paper": { maxWidth: 300, width: "100%" },
                    }}
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1, fontSize: "0.9rem", opacity: 0.8, textAlign: "center" }}>
                      {mediaHeight === "Short" && cardWidth === "Small" &&
                        "The image you specify must be 4:3 aspect ratio, have a max file size of 1 MB, have an optimal resolution of 960 pixels x 720 pixels, and should be a JPG,JPEG,PNG, or GIF. If you are uploading a video, the max file size is 5 MB."}
                      {mediaHeight === "Medium" && cardWidth === "Medium" &&
                        "The image you specify must be 4:3 aspect ratio, have a max file size of 1 MB, have an optimal resolution of 1440 pixels x 1080 pixels, and should be a JPG,JPEG,PNG, or GIF. If you are uploading a video, the max file size is 5 MB"}
                      {mediaHeight === "Short" && cardWidth === "Medium" &&
                        "The image you specify must be 4:5 aspect ratio, have a max file size of 1 MB, have an optimal resolution of 576 pixels x 720 pixels, and should be a JPG,JPEG,PNG, or GIF. If you are uploading a video, the max file size is 5 MB"}
                      {mediaHeight === "Medium" && cardWidth === "Small" &&
                        "The image you specify must be 2:1 aspect ratio, have a max file size of 1 MB, have an optimal resolution of 1440 pixels x 720 pixels, and should be a JPG,JPEG,PNG, or GIF. If you are uploading a video, the max file size is 5 MB."}
                      {mediaHeight === "" && cardWidth === "" && "choose media height and card width"}
                    </Typography>
                  </Popover>
                </Box>

                <UploadButton
                  label="Upload File"
                  accept=".jpeg,.jpg,.png,.gif,video/*"
                  inputKey={`media-${activeCardId}`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    const isImage = /\.(jpe?g|png|gif)$/i.test(file.name);
                    const isVideo = /^video\//i.test(file.type);

                    if (!isImage && !isVideo) {
                      toast.error("Only JPG, JPEG, PNG, GIF images or videos are allowed.");
                      resetFileInput(e);
                      return;
                    }

                    if (isImage && file.size > 1 * 1024 * 1024) {
                      toast.error("Image must be under 1 MB.");
                      resetFileInput(e);
                      return;
                    }

                    if (isVideo && file.size > 10 * 1024 * 1024) {
                      toast.error("Video must be under 10 MB.");
                      resetFileInput(e);
                      return;
                    }

                    if (isImage) {
                      const img = new Image();
                      img.src = URL.createObjectURL(file);
                      img.onload = async () => {
                        try {
                          await img.decode();
                          const w = img.width;
                          const h = img.height;
                          URL.revokeObjectURL(img.src);

                          let isValid = false;
                          if (mediaHeight === "Short" && cardWidth === "Small") {
                            isValid = w === 960 && h === 720;
                          } else if (mediaHeight === "Medium" && cardWidth === "Medium") {
                            isValid = w === 1440 && h === 1080;
                          } else if (mediaHeight === "Short" && cardWidth === "Medium") {
                            isValid = w === 576 && h === 720;
                          } else if (mediaHeight === "Medium" && cardWidth === "Small") {
                            isValid = w === 1440 && h === 720;
                          } else {
                            toast.error("Please select Media Height and Card Width first.");
                            resetFileInput(e);
                            return;
                          }

                          if (!isValid) {
                            toast.error(`Invalid image dimensions! Must be exact. Got: ${w} x ${h}`);
                            resetFileInput(e);
                            return;
                          }

                          updateActiveCard("fileName", file.name);
                          updateActiveCard("mediaFile", file);
                          updateActiveCard("media_type", "image");
                          updateActiveCard("thumbnailFile", null);
                          updateActiveCard("thumbnailFileName", "");
                        } catch (err) {
                          alert("Could not decode image for dimension check.");
                          resetFileInput(e);
                        }
                      };
                      img.onerror = () => {
                        toast.error("Could not load image for dimension check.");
                        resetFileInput(e);
                      };
                    } else {
                      updateActiveCard("fileName", file.name);
                      updateActiveCard("mediaFile", file);
                      updateActiveCard("media_type", "video");
                    }
                  }}
                />
                <FileNameText
                  name={getActiveCard()?.fileName}
                  placeholder="No file selected"
                />
              </Box>

              {getActiveCard()?.media_type === "video" && (
                <Box sx={{ ...rowSx, alignItems: "center", mt: 0.75 }}>
                  <Typography sx={{ fontSize: FS, fontWeight: 600 }}>
                    Video Thumbnail * (max 100KB, 3:1)
                  </Typography>
                  <UploadButton
                    label="Upload"
                    accept=".jpeg,.jpg,.png"
                    inputKey={`thumb-${activeCardId}`}
                    onChange={handleCarouselThumbnailChange}
                  />
                  <FileNameText
                    name={getActiveCard()?.thumbnailFileName}
                    placeholder="No thumbnail selected"
                  />
                </Box>
              )}

              {/* Card Title - shortened width, single line */}
              <TextField
                label="Card Title"
                variant="outlined"
                value={getActiveCard().title}
                size="small"
                sx={{ ...fieldSx, mt: 0.75, width: 300, maxWidth: "100%" }}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 200) {
                    updateActiveCard("title", newValue);
                  }
                }}
                required
                helperText={variableHelper(
                  () => {
                    const currentTitle = getActiveCard().title || "";
                    if ((currentTitle.match(/\[/g) || []).length >= 10) return;
                    updateActiveCard(
                      "title",
                      currentTitle +
                        getNextVariable(
                          cards
                            .map((c) => `${c.title} ${c.carouselCardDescription}`)
                            .join(" ")
                        )
                    );
                  },
                  getActiveCard().title.length,
                  200
                )}
              />

              <TextField
                fullWidth
                name="cardDescription"
                variant="outlined"
                label="Card Description"
                rows={3}
                size="small"
                sx={{ ...fieldSx, mt: 0.75 }}
                multiline
                value={getActiveCard().carouselCardDescription}
                required
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 2500) {
                    updateActiveCard("carouselCardDescription", newValue);
                  }
                }}
                helperText={variableHelper(
                  () => {
                    const current = getActiveCard().carouselCardDescription || "";
                    if ((current.match(/\[/g) || []).length >= 10) return;
                    updateActiveCard(
                      "carouselCardDescription",
                      current +
                        getNextVariable(
                          cards
                            .map((c) => `${c.title} ${c.carouselCardDescription}`)
                            .join(" ")
                        )
                    );
                  },
                  getActiveCard().carouselCardDescription.length,
                  2500
                )}
              />
            </SectionCard>
          )}

          {/* ── Suggested actions / reply buttons ── */}
          <SectionCard
            title="Suggested Actions / Reply Buttons"
            action={
              suggestions.length < SUGGESTION_LIMIT && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={handleAdd}
                  sx={{ ...outlinedPurpleBtn, height: 30 }}
                >
                  Add Suggestion
                </Button>
              )
            }
          >
            <Stack spacing={0.75}>
              {suggestions.map((s) => (
                <Box
                  key={s.button_no}
                  sx={{
                    ...rowSx,
                    position: "relative",
                    p: 1,
                    pr: 4.5,
                    background: "rgba(255,255,255,0.5)",
                    borderRadius: "8px",
                    border: "1px solid #e5defa",
                  }}
                >
                  <FormControl size="small" sx={{ ...smallSx, width: 180 }}>
                    <InputLabel>Type of action</InputLabel>
                    <Select
                      value={s.button_action_id}
                      label="Type of action"
                      onChange={(e) =>
                        handlesuggetionChange(s.button_no, "button_action_id", e.target.value)
                      }
                    >
                      {getSuggestions?.data?.map((item) => (
                        <MenuItem
                          key={item.button_action_id}
                          value={item.button_action_id}
                          sx={menuItemSx}
                        >
                          {item.action_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Suggestion Text"
                    size="small"
                    sx={{ ...smallSx, width: 230 }}
                    value={s.suggestion_text}
                    onChange={(e) => {
                      const value = e.target.value;
                      handlesuggetionChange(s.button_no, "suggestion_text", value);
                      if (errors[`${s.button_no}_suggestion_text`] && value.trim() !== "") {
                        setErrors((prev) => ({ ...prev, [`${s.button_no}_suggestion_text`]: "" }));
                      }
                    }}
                    onBlur={() => {
                      if (!s.suggestion_text || s.suggestion_text.trim() === "") {
                        setErrors((prev) => ({
                          ...prev,
                          [`${s.button_no}_suggestion_text`]: "Suggestion Text is required",
                        }));
                      }
                    }}
                    inputProps={{ maxLength: 120 }}
                    error={Boolean(errors[`${s.button_no}_suggestion_text`])}
                    helperText={suggestionHelper(`${s.button_no}_suggestion_text`)}
                  />

                  <TextField
                    label="Suggestion Postback"
                    placeholder="Enter postback data"
                    size="small"
                    sx={{ ...smallSx, width: 230 }}
                    value={s.suggestion_postback}
                    onChange={(e) => {
                      const value = e.target.value;
                      handlesuggetionChange(s.button_no, "suggestion_postback", value);
                      if (errors[`${s.button_no}_suggestion_postback`] && value.trim() !== "") {
                        setErrors((prev) => ({ ...prev, [`${s.button_no}_suggestion_postback`]: "" }));
                      }
                    }}
                    onBlur={() => {
                      if (!s.suggestion_postback || s.suggestion_postback.trim() === "") {
                        setErrors((prev) => ({
                          ...prev,
                          [`${s.button_no}_suggestion_postback`]: "Suggestion Postback is required",
                        }));
                      }
                    }}
                    inputProps={{ maxLength: 120 }}
                    error={Boolean(errors[`${s.button_no}_suggestion_postback`])}
                    helperText={suggestionHelper(`${s.button_no}_suggestion_postback`)}
                  />

                  {/* Fields that depend on the action type */}
                  {s.button_action_id === 2 && (
                    <>
                      <TextField
                        type="url"
                        label="URL"
                        value={s.url}
                        size="small"
                        sx={{ ...smallSx, width: 230 }}
                        onChange={(e) => {
                          const value = e.target.value;
                          handlesuggetionChange(s.button_no, "url", value);
                          if (errors[`${s.button_no}_url`] && value.trim() !== "") {
                            setErrors((prev) => ({ ...prev, [`${s.button_no}_url`]: "" }));
                          }
                        }}
                        onBlur={() => {
                          const value = s.url?.trim() || "";
                          let message = "";
                          if (value === "") message = "URL required";
                          else if (!isValidUrl(value)) message = "Write correct URL";
                          setErrors((prev) => ({ ...prev, [`${s.button_no}_url`]: message }));
                        }}
                        inputProps={{ maxLength: 120 }}
                        error={Boolean(errors[`${s.button_no}_url`])}
                        helperText={errors[`${s.button_no}_url`] || ""}
                      />
                      <FormControl size="small" sx={{ ...smallSx, width: 160 }}>
                        <InputLabel>Application</InputLabel>
                        <Select
                          value={s.application}
                          label="Application"
                          onChange={(e) =>
                            handlesuggetionChange(s.button_no, "application", e.target.value)
                          }
                        >
                          <MenuItem value="browser" sx={menuItemSx}>Browser</MenuItem>
                          <MenuItem value="application" sx={menuItemSx}>Application</MenuItem>
                        </Select>
                        {errors[`${s.button_no}_application`] && (
                          <FormHelperText>{errors[`${s.button_no}_application`]}</FormHelperText>
                        )}
                      </FormControl>
                    </>
                  )}

                  {s.button_action_id === 3 && (
                    <TextField
                      label="Dialer"
                      value={s.phone_number || "+91"}
                      size="small"
                      sx={{ ...smallSx, width: 200 }}
                      onChange={(e) => {
                        let value = e.target.value;
                        value = value.replace(/[^+\d]/g, "");
                        if (!value.startsWith("+91")) {
                          value = "+91" + value.replace(/^(\+?91)?/, "");
                        }
                        const digits = value.slice(3).replace(/\D/g, "").slice(0, 10);
                        value = "+91" + digits;
                        handlesuggetionChange(s.button_no, "phone_number", value);
                        if (errors[`${s.button_no}_phone_number`] && digits.length > 0) {
                          setErrors((prev) => ({ ...prev, [`${s.button_no}_phone_number`]: "" }));
                        }
                      }}
                      onBlur={() => {
                        if (!s.phone_number || s.phone_number.length !== 13) {
                          setErrors((prev) => ({
                            ...prev,
                            [`${s.button_no}_phone_number`]: "Valid phone number is required",
                          }));
                        }
                      }}
                      inputProps={{ maxLength: 13 }}
                      error={Boolean(errors[`${s.button_no}_phone_number`])}
                      helperText={errors[`${s.button_no}_phone_number`] || ""}
                    />
                  )}

                  {s.button_action_id === 4 && (
                    <>
                      <TextField
                        label="Latitude"
                        value={s.latitude ?? ""}
                        size="small"
                        sx={{ ...smallSx, width: 150 }}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[0-9]{0,2}(\.[0-9]*)?$/.test(value)) {
                            const parts = value.split(".");
                            if (parts[0].length <= 2) {
                              handlesuggetionChange(s.button_no, "latitude", value);
                              if (errors[`${s.button_no}_latitude`] && value.trim() !== "") {
                                setErrors((prev) => ({ ...prev, [`${s.button_no}_latitude`]: "" }));
                              }
                            }
                          }
                        }}
                        onBlur={() => {
                          const raw = (s.latitude ?? "").trim();
                          const parts = raw.split(".");
                          if (!raw) {
                            setErrors((prev) => ({ ...prev, [`${s.button_no}_latitude`]: "This field is required" }));
                          } else if (parts.length !== 2 || !parts[0] || parts[0].length > 2 || !parts[1]) {
                            setErrors((prev) => ({ ...prev, [`${s.button_no}_latitude`]: "Must be like 12.34 (dot required)" }));
                          }
                        }}
                        inputProps={{ inputMode: "decimal" }}
                        error={Boolean(errors[`${s.button_no}_latitude`])}
                        helperText={errors[`${s.button_no}_latitude`] || ""}
                      />
                      <TextField
                        label="Longitude"
                        value={s.longitude ?? ""}
                        size="small"
                        sx={{ ...smallSx, width: 150 }}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[0-9]{0,2}(\.[0-9]*)?$/.test(value)) {
                            const parts = value.split(".");
                            if (parts[0].length <= 2) {
                              handlesuggetionChange(s.button_no, "longitude", value);
                              if (errors[`${s.button_no}_longitude`] && value.trim() !== "") {
                                setErrors((prev) => ({ ...prev, [`${s.button_no}_longitude`]: "" }));
                              }
                            }
                          }
                        }}
                        onBlur={() => {
                          const raw = (s.longitude ?? "").trim();
                          const parts = raw.split(".");
                          if (!raw) {
                            setErrors((prev) => ({ ...prev, [`${s.button_no}_longitude`]: "This field is required" }));
                          } else if (parts.length !== 2 || !parts[0] || parts[0].length > 2 || !parts[1]) {
                            setErrors((prev) => ({ ...prev, [`${s.button_no}_longitude`]: "Must be like 12.34 (dot required)" }));
                          }
                        }}
                        inputProps={{ inputMode: "decimal" }}
                        error={Boolean(errors[`${s.button_no}_longitude`])}
                        helperText={errors[`${s.button_no}_longitude`] || ""}
                      />
                    </>
                  )}

                  {s.button_action_id === 5 && (
                    <TextField
                      label="Query"
                      value={s.query || ""}
                      size="small"
                      sx={{ ...smallSx, width: 220 }}
                      onChange={(e) => {
                        const value = e.target.value;
                        handlesuggetionChange(s.button_no, "query", value);
                        if (errors[`${s.button_no}_query`] && value.trim() !== "") {
                          setErrors((prev) => ({ ...prev, [`${s.button_no}_query`]: "" }));
                        }
                      }}
                      onBlur={() => {
                        if (!s.query || s.query.trim() === "") {
                          setErrors((prev) => ({
                            ...prev,
                            [`${s.button_no}_query`]: "Query is required",
                          }));
                        }
                      }}
                      error={Boolean(errors[`${s.button_no}_query`])}
                      helperText={errors[`${s.button_no}_query`] || ""}
                    />
                  )}

                  {s.button_action_id === 6 && (
                    <TextField
                      label="Location Query"
                      value={s.location_query}
                      size="small"
                      sx={{ ...smallSx, width: 220 }}
                      onChange={(e) => {
                        const value = e.target.value;
                        handlesuggetionChange(s.button_no, "location_query", value);
                        if (errors[`${s.button_no}_location_query`] && value.trim() !== "") {
                          setErrors((prev) => ({ ...prev, [`${s.button_no}_location_query`]: "" }));
                        }
                      }}
                      onBlur={() => {
                        if (!s.location_query || s.location_query.trim() === "") {
                          setErrors((prev) => ({
                            ...prev,
                            [`${s.button_no}_location_query`]: "Location query is required",
                          }));
                        }
                      }}
                      inputProps={{ maxLength: 10 }}
                      error={Boolean(errors[`${s.button_no}_location_query`])}
                      helperText={suggestionHelper(`${s.button_no}_location_query`)}
                    />
                  )}

                  {s.button_action_id === 7 && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        label="Start Date Time"
                        value={s.start_date_time}
                        size="small"
                        sx={{ ...smallSx, width: 220 }}
                        minDateTime={dayjs()}
                        onChange={(newValue) =>
                          handlesuggetionChange(s.button_no, "start_date_time", newValue)
                        }
                        renderInput={(params) => <TextField fullWidth {...params} sx={smallSx} />}
                      />
                      <DateTimePicker
                        label="End Date Time"
                        value={s.end_date_time}
                        size="small"
                        sx={{ ...smallSx, width: 220 }}
                        minDateTime={s.start_date_time || dayjs()}
                        onChange={(newValue) =>
                          handlesuggetionChange(s.button_no, "end_date_time", newValue)
                        }
                        renderInput={(params) => <TextField fullWidth {...params} sx={smallSx} />}
                      />
                      <TextField
                        label="Title"
                        value={s.calendar_title}
                        size="small"
                        sx={{ ...smallSx, width: 200 }}
                        onChange={(e) => {
                          const value = e.target.value;
                          handlesuggetionChange(s.button_no, "calendar_title", value);
                          if (errors[`${s.button_no}_calendar_title`] && value.trim() !== "") {
                            setErrors((prev) => ({ ...prev, [`${s.button_no}_calendar_title`]: "" }));
                          }
                        }}
                        onBlur={() => {
                          if (!s.calendar_title || s.calendar_title.trim() === "") {
                            setErrors((prev) => ({
                              ...prev,
                              [`${s.button_no}_calendar_title`]: "Title is required",
                            }));
                          }
                        }}
                        error={Boolean(errors[`${s.button_no}_calendar_title`])}
                        helperText={suggestionHelper(`${s.button_no}_calendar_title`)}
                      />
                      <TextField
                        label="Description"
                        value={s.calendar_description}
                        size="small"
                        sx={{ ...smallSx, width: 220 }}
                        onChange={(e) => {
                          const value = e.target.value;
                          handlesuggetionChange(s.button_no, "calendar_description", value);
                          if (errors[`${s.button_no}_calendar_description`] && value.trim() !== "") {
                            setErrors((prev) => ({ ...prev, [`${s.button_no}_calendar_description`]: "" }));
                          }
                        }}
                        onBlur={() => {
                          if (!s.calendar_description || s.calendar_description.trim() === "") {
                            setErrors((prev) => ({
                              ...prev,
                              [`${s.button_no}_calendar_description`]: "Description is required",
                            }));
                          }
                        }}
                        error={Boolean(errors[`${s.button_no}_calendar_description`])}
                        helperText={suggestionHelper(`${s.button_no}_calendar_description`)}
                      />
                    </LocalizationProvider>
                  )}

                  <IconButton
                    size="small"
                    onClick={() => handleRemove(s.button_no)}
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      color: "#ef4444",
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                      width: 26,
                      height: 26,
                      "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.2)" },
                      "& .MuiSvgIcon-root": { fontSize: 16 },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </SectionCard>

          {/* Submit / Cancel on the left */}
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading}
              sx={pillBtn}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{ ...outlinedPurpleBtn, borderRadius: "20px", px: 2.5, height: 34 }}
            >
              Cancel
            </Button>
          </Box>
        </Paper>

        {/* ── Right: phone preview ── */}
        <Box sx={{ width: 266, mx: "auto", position: "sticky", top: 8 }}>
          <Box
            sx={{
              width: 260,
              height: 470,
              margin: "0 auto",
              border: "3px solid #0f172a",
              borderRadius: "28px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              background: "#f4f4f5",
              boxShadow: "0 10px 28px rgba(15,23,42,0.25)",
            }}
          >
            {/* Status bar */}
            <Box
              sx={{
                height: 28,
                px: 1.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#0f172a",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#fbbf24" }}>
                {currentTime}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <WifiIcon sx={{ fontSize: 14, color: "#e2e8f0" }} />
                <NetworkCellIcon sx={{ fontSize: 14, color: "#e2e8f0" }} />
                <BatteryFullIcon sx={{ fontSize: 14, color: "#e2e8f0" }} />
              </Stack>
            </Box>

            {/* Sender header */}
            <Box
              sx={{
                height: 40,
                px: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: "#f1f5f9",
                borderBottom: "1px solid #e2e8f0",
                flexShrink: 0,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18, color: "#cbd5e1" }} />
              <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#0f172a" }}>
                {agent.length > 0 ? agent[0].agent_name : "Business"}
              </Typography>
            </Box>

            {/* Message area */}
            <Box
              sx={{
                flex: 1,
                p: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": { background: "#CBD5F5", borderRadius: "10px" },
              }}
            >
              {selectedTemplate?.name === "text" && (
                <Box
                  sx={{
                    bgcolor: "#E0F2FE",
                    borderRadius: "14px",
                    px: 1.2,
                    py: 1,
                    maxWidth: "85%",
                    ml: "auto",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    fontSize: "0.8rem",
                    lineHeight: 1.4,
                    whiteSpace: "pre-line",
                    overflowWrap: "break-word",
                  }}
                >
                  {smstext.trim() ? smstext : <TypingIndicator />}
                </Box>
              )}
              {selectedTemplate?.name === "text" && suggestions.length > 0 && (
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.5 }}>
                  {suggestions.map((item, idx) => (
                    <Button
                      key={idx}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.7rem",
                        textTransform: "none",
                        p: "2px 8px",
                        borderRadius: "10px",
                        background: "#fff",
                      }}
                    >
                      {item.suggestion_text || "Action"}
                    </Button>
                  ))}
                </Box>
              )}

              {selectedTemplate?.name === "Rich Card" && (
                <Box sx={{ maxWidth: "100%" }}>
                  {fileUrl && (
                    <Box sx={{ mb: 1, borderRadius: "12px", overflow: "hidden" }}>
                      {fileType === "image" ? (
                        <img
                          src={fileUrl}
                          alt="rich card media"
                          style={{ width: "100%", maxHeight: "180px", objectFit: "cover" }}
                        />
                      ) : (
                        <video src={fileUrl} controls style={{ width: "100%", maxHeight: "180px" }} />
                      )}
                    </Box>
                  )}
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: "12px",
                      p: 1.25,
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        mb: 0.5,
                        fontSize: "0.8rem",
                        overflowWrap: "break-word",
                      }}
                    >
                      {cardTitle || "Card Title"}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.78rem", whiteSpace: "pre-line" }}>
                      {cardDescription || "Card description will appear here"}
                    </Typography>
                    {suggestions.length > 0 && (
                      <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {suggestions.map((item, idx) => (
                          <Button
                            key={idx}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", textTransform: "none", p: "2px 8px" }}
                          >
                            {item.suggestion_text}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {selectedTemplate?.name === "Carousel" && cards.length > 0 && (
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <Box sx={{ height: "140px", overflow: "hidden", backgroundColor: "#000" }}>
                      {(() => {
                        const card = getActiveCard();
                        if (!card?.mediaFile || !activeMediaPreviewUrl) {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                color: "#fff",
                              }}
                            >
                              <Typography variant="caption">No Media</Typography>
                            </Box>
                          );
                        }
                        const isImage = card.mediaFile.type.startsWith("image/");
                        if (isImage) {
                          return (
                            <img
                              src={activeMediaPreviewUrl}
                              alt="carousel media"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          );
                        } else {
                          return (
                            <video
                              src={activeMediaPreviewUrl}
                              poster={activeThumbnailPreviewUrl}
                              controls
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          );
                        }
                      })()}
                    </Box>
                    <Box sx={{ p: 1.25 }}>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          mb: 0.5,
                          fontSize: "0.8rem",
                          overflowWrap: "break-word",
                        }}
                      >
                        {getActiveCard()?.title || "Card Title"}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.78rem", whiteSpace: "pre-line" }}>
                        {getActiveCard()?.carouselCardDescription || "Card description"}
                      </Typography>
                      {suggestions.length > 0 && (
                        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {suggestions.map((item, idx) => (
                            <Button
                              key={idx}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", textTransform: "none", p: "2px 8px" }}
                            >
                              {item.suggestion_text}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {cards.length > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
                      {cards.map((_, idx) => (
                        <Box
                          key={idx}
                          onClick={() => setActiveCardId(cards[idx].id)}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: cards[idx].id === activeCardId ? PURPLE : "#ccc",
                            cursor: "pointer",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {!selectedTemplate?.name && (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#334155",
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}>
                    Select a template type
                    <br />
                    to preview
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateForm;